import { useEffect, useRef } from "react";
import classNames from "classnames";
import { Carousel, type CarouselRefAttributes } from "~/common/components/carousel/Carousel";
import Widget from "./Widget";
import classes from "../CMSHomepagePersonalization.module.less";
import { Stack, styled } from "@mui/material";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import type { CMSBannerDTO } from "~/CMSComponents/dto";

export interface RotatingWidgetsProps {
  /**
   * Time in milliseconds when the slide should change
   */
  timeout?: number;
  /**
   * A valid MArketing Widget properties object
   */
  banners: CMSBannerDTO[];
  className?: string;
}

export const RotatingWidgets = ({ banners, timeout = 3000, className }: RotatingWidgetsProps) => {
  const carouselRef = useRef<CarouselRefAttributes>(null);

  const pageAmount = banners.length;
  const isDesktop = useIsMinDesktopSize();

  useEffect(() => {
    if (pageAmount > 1) {
      const intervalId = setInterval(() => {
        if (carouselRef.current) {
          carouselRef.current.setNextPage();
        }
      }, timeout);
      return () => clearInterval(intervalId);
    }
  }, [timeout, pageAmount]);

  return (
    <Carousel
      className={classNames("scha__rotating-banners", className)}
      data={banners}
      pageItemsCount={isDesktop ? 2 : 1}
      renderSlide={({ data }) => {
        return (
          <WidgetsContainer halfWidth={data.length == 1 && isDesktop}>
            {data.map((banner, index) => (
              <Widget key={index} data={banner} />
            ))}
          </WidgetsContainer>
        );
      }}
      ref={carouselRef}
      continuous={true}
      paginationItemActiveClassName={classes.paginationItemActive}
      paginationItemClassName={classes.paginationItem}
      hideArrows
    />
  );
};

const WidgetsContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "halfWidth",
})<{ halfWidth: boolean }>(({ halfWidth }) => ({
  flexDirection: "row",
  gap: "24px",
  width: halfWidth ? "50%" : "100%",
}));
